import CdnImagePropsMixin from "./CdnImagePropsMixin";
import {getDefaultWidthByBreakpoint} from "../../plugins/breakpoints";
import LazyImageLoader from "../../services/LazyImageLoader";

export default {
    mixins: [CdnImagePropsMixin],

    props: {

        // Whether to activate an image zoom
        zoom: {
            type: Boolean,
            default: false
        },

        // The background color to use (or true for a slight gray) when the contained image does not fit the aspect ratio
        backgroundColor: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            // The image source url to load the image from
            imageSrc: null,

            // A set of available responsive sources for the image based on the device screen
            imageSrcSets: [],

            // The lozad observer for lazy loading images
            loadingObserver: null,

            // Whether the image url is currently being loaded
            isLoading: true
        }
    },

    computed: {

        imagePath() {
            return typeof this.src === 'string' ? this.src : this.src?.path;
        },

        currentSizeBreakpoint() {
            if (this.$breakpoint.xlOnly && this.size && this.size.xl) {
                return 'xl';
            } else if (this.$breakpoint.lgAndUp && this.size && this.size.lg) {
                return 'lg';
            } else if (this.$breakpoint.mdAndUp && this.size && this.size.md) {
                return 'md';
            } else if (this.$breakpoint.smAndUp && this.size && this.size.sm) {
                return 'sm';
            }

            return 'xs';
        },

        currentSize() {
            switch (this.currentSizeBreakpoint) {
                case 'xl':
                    return this.size.xl;
                case 'lg':
                    return this.size.lg;
                case 'md':
                    return this.size.md;
                case 'sm':
                    return this.size.sm;
            }
            return this.size;
        },

        currentDimensions() {
            let height = this.currentSize.height ? this.currentSize.height : null;
            let width = this.currentSize.width ? this.currentSize.width : null;

            // Parse relative values (only width supported)
            if (width && typeof width === 'string'){
                if (width.endsWith('vw')) {
                    const relativeWidth = parseInt(width.slice(0, -2));
                    const deviceWidth = getDefaultWidthByBreakpoint(this.$breakpoint.current);
                    width = Math.round(deviceWidth * (relativeWidth / 100));
                } else {
                    width = parseInt(width);
                }
            }

            const ratio = this.currentSize.ratio ? this.currentSize.ratio : (this.size ? this.size.ratio : null);
            height = height ? height : (ratio ? (Math.round((width / ratio) * 100) / 100) : null);
            width = width ? width : (ratio ? (Math.round((height * ratio) * 100) / 100) : null);

            return {
                height: height,
                width: width,
                ratio: ratio ? ratio : (width && height ? (Math.round((width / height) * 100) / 100) : null)
            }
        }
    },

    watch: {
        currentDimensions() {
            this.refreshImageSrc();
        },

        imageSrc(newSrc, oldSrc) {

            // If the image url was already lazy loaded, we need to replace the src manually if it changes
            if (oldSrc && newSrc !== oldSrc && !this.isLoading) {
                this.setImageSourcesOnImageElement(newSrc);
            }
        },

        src() {
            this.refreshImageSrc();
        }
    },

    created() {

        // We set the initial image url asap to support the 'immediate' prop for optimized LCP
        if (this.immediate) {
            this.refreshImageSrc();
            this.isLoading = false
        }
    },

    mounted() {
        if (!this.immediate) {
            this.refreshImageSrc();
            this.loadingObserver = LazyImageLoader(this.$refs.image, {
                loaded: () => {
                    this.isLoading = false;
                }
            });
            this.loadingObserver.observe();
        }
    },

    methods: {
        refreshImageSrc() {
            this.imageSrc = this.$image.format(this.src, {
                width: this.currentDimensions.width,
                height: this.currentDimensions.height,
                fit: this.cover ? 'cover' : 'contain'
            });

            // Specific extensions do not allow modifications
            if (!this.currentDimensions.width || this.imagePath.endsWith('.svg') || this.imagePath.endsWith('.gif')) {
                this.imageSrcSets = [];

                return;
            }

            // We use '1x' and '2x' dimensions for each specified size to support devices with a higher dpr
            const dpr = [1, 2];
            const srcSetData = dpr.map(pixelRatio => {
                const dprWidth = this.currentDimensions.width * pixelRatio;
                const requestWidth = this.maxWidth ? Math.min(this.maxWidth, dprWidth) : dprWidth;

                return {
                    dpr: pixelRatio,
                    width: Math.round(requestWidth),
                    url: this.$image.format(this.src, {
                        width: requestWidth,
                        height: Math.round((requestWidth / this.currentDimensions.ratio) * 100) / 100,
                        fit: this.cover ? 'cover' : 'contain'
                    })
                }
            });

            // Filter duplicates due to the image's max width
            this.imageSrcSets = srcSetData.filter((srcSet, index, self) => self.findIndex(selfSrcSet => selfSrcSet.width === srcSet.width) === index);
        },

        /**
         * Natively refreshes the image source to trigger a reload by the browser as this does not work via Vue.js.
         * This should be overridden by every consumer component.
         */
        setImageSourcesOnImageElement() {
            if (this.$refs.image) {
                this.$refs.image.src = this.imageSrc;
            }
        },
    }

};

